import React from 'react';
import { Card, formatCurrency, Button } from '@avila-tek/ui';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { AddIcon, MinusIcon } from '@avila-tek/ui/src';
import { Company, Order } from '../../../models';
import {
  ADD_BENEFIT_TO_SHOP_CART,
  CREATE_ORDER,
} from '../../../graphql/mutation';
import { useNotify, useShopCart, useUser } from '../../../hooks';

function PointInfo() {
  const [disable, setDisable] = React.useState<boolean>(false);
  const [quantity, setQuantity] = React.useState<number>(1);
  const [shopCart, _, refreshShopCart] = useShopCart();
  const router = useRouter();
  const notify = useNotify();
  const [user] = useUser();

  const [addBenefitToShopCartMutation] = useMutation<{
    addBenefitsToShopCart: { _id: string };
  }>(ADD_BENEFIT_TO_SHOP_CART);

  const [createOrderMutation, { loading }] = useMutation<{
    createOrder: Order;
  }>(CREATE_ORDER);

  const handleAddpointsToCart = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      if (quantity < 1) {
        notify('La cantidad no puede ser menor a 1', 'error');
        return;
      }
      if (disable) return;
      setDisable(true);

      const { data: dataAddItemToCart } = await addBenefitToShopCartMutation({
        variables: {
          data: {
            shopCartId: shopCart?._id,
            companyId: (user?.company as Company)?._id,
            benefitId: router.query.benefitId,
            quantity,
          },
        },
      });
      if (dataAddItemToCart?.addBenefitsToShopCart?._id) {
        createOrder(e);
      }
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    }
  };

  const createOrder = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      const { data } = await createOrderMutation({
        variables: {
          data: {
            shopCartId: shopCart?._id,
            promoCodes: [],
            billingAddress: (user?.company as Company)?.addresses[0]?._id,
            paymentInfo: {
              paymentMethod: null,
              amount: parseInt(router.query.benefitValue as string) * quantity,
              reference: null,
            },
            status: 'receivable',
          },
        },
      });
      if (data?.createOrder._id) {
        await refreshShopCart();
        notify('Puntos solicitados', 'success');
        router.push('/app/catalogue');
        setDisable(false);
      } else {
        notify('Error al solicitar puntos, intente de nuevo', 'error');
      }
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
      setDisable(false);
    }
  };

  const handleInputChange = (event) => {
    const value = parseInt(event.target.value);
    if (value < 0 || Number.isNaN(value)) {
      setQuantity(1);
      return;
    }
    if (value > 100000) {
      notify('La cantidad de puntos no puede ser mayor a 100.000', 'info');
      setQuantity(100000);
      return;
    }
    setQuantity(value);
  };

  const handleIncrement = () => {
    setQuantity((prev) => prev + 1);
  };

  const handleDecrement = () => {
    if (quantity > 0) {
      setQuantity((prev) => prev - 1);
    }
  };
  return (
    <Card className="shadow-sm">
      <Card.Body>
        {/* title */}
        <div>
          <h2 className="text-xl sm:text-3xl text-text-black">
            Puntos prepagados
          </h2>
          <h2 className="text-xl text-neutral-cold-400 mt-1 font-arial font-semibold">
            {(user.company as Company)?.name}
          </h2>
        </div>
        {/* quantity and price */}
        <div className="flex my-4 flex-col sm:flex-row items-center justify-end gap-2">
          <span className="text-purple-300 text-xl font-extrabold">
            ${formatCurrency(router.query.benefitValue as string)}
          </span>
          <h2 className="text-base text-text-black">
            <span className="text-xs">x</span> punto
          </h2>
        </div>
        {/* Buttons */}
        <div className="w-full flex flex-col sm:flex-row items-center justify-end gap-5 mb-5">
          <h1>
            Valor nominal $
            {formatCurrency(
              quantity * parseInt(router.query.benefitValue as string)
            )}
          </h1>
          <div className="order-1 sm:order-2">
            <div className="w-full flex justify-center items-center gap-5">
              <div className="text-neutral-cold-400">Utilizar</div>
              <div className="text-neutral-cold-700">
                <div className="flex flex-row items-center justify-between rounded-md border border-neutral-cold-400 w-32">
                  <button
                    className="p-2 placeholder:text-black cursor-pointer"
                    type="button"
                    onClick={handleDecrement}
                  >
                    <MinusIcon className="w-5 h-5 text-neutral-500 hover:text-color-primary-text-100" />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={handleInputChange}
                    className="w-full p-2 text-center outline-none border-none"
                  />
                  <button
                    className="p-2 placeholder:text-black cursor-pointer"
                    type="button"
                    onClick={handleIncrement}
                  >
                    <AddIcon className="w-5 h-5 text-neutral-500 hover:text-color-primary-text-100" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Button
          className={`bg-neutral-warm-200 hover:bg-purple-200 ${
            disable ? 'cursor-not-allowed' : ''
          }`}
          disabled={disable}
          onClick={(e) => {
            handleAddpointsToCart(e);
          }}
        >
          {disable ? 'Procesando...' : 'Solicitar puntos'}
        </Button>
      </Card.Body>
    </Card>
  );
}

export default PointInfo;
